import { memo, useCallback } from 'react';
import { FormikProps } from 'formik';
import _get from 'lodash.get';
import { parse, format, isValid } from 'date-fns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useStyles } from './inputs.styles';
import { TextFieldProps } from '@mui/material';

interface FormikTimeInputProps {
  name: string;
  label?: string;
  type?: string;
  formikProps: FormikProps<any>;
  handleChange?: (event: { target: { name: string; value: any } }) => void;
  handleBlur?: (event: React.FocusEvent<any>) => void;
  className?: string;
  required?: boolean;
  textFieldProps?: TextFieldProps;
  resultsFormat?: 'string' | 'date';
  validationKey?: string;
  [key: string]: any;
}

export const FormikTimeInput = memo(function FormikTimeInput({
  name,
  label,
  type = 'text',
  formikProps,
  handleChange,
  handleBlur,
  className,
  required,
  textFieldProps: _textFieldProps,
  resultsFormat = 'string',
  validationKey,
  ...otherProps
}: FormikTimeInputProps) {
  const { classes, cx } = useStyles();
  const {
    handleChange: formikHandleChange,
    handleBlur: formikHandleBlur,
    values,
    touched,
    errors,
  } = formikProps;

  const textFieldProps = _textFieldProps || {};

  const onBlur = useCallback(
    (e: React.FocusEvent<any>) => {
      // Create a new event with the field name explicitly set
      // https://formik.org/docs/api/formik#handleblur-e-any--void
      const eventWithName = {
        ...e,
        target: {
          ...e.target,
          name,
        },
      };
      formikHandleBlur(eventWithName);
      handleBlur?.(eventWithName);
    },
    [formikHandleBlur, handleBlur, name],
  );

  const onChange = useCallback(
    (_value: any) => {
      let value = _value;
      if (resultsFormat === 'string') {
        value = isValid(_value) ? format(_value, 'h:mm a') : '';
      }
      const event = { target: { name, value } };
      formikHandleChange(event);
      handleChange && handleChange(event);
    },
    [formikHandleChange, handleChange, name, resultsFormat],
  );

  // On blur touched is set on the name but on submit it's set on the nested object so we need to check both
  const isTouched = !!(
    _get(touched, name) || _get(touched, validationKey as string)
  );
  const errorText = _get(errors, validationKey ?? name) as string;

  return (
    <TimePicker
      name={name}
      value={
        (resultsFormat === 'string'
          ? dateStringToLocalDate(values[name])
          : values[name]) ?? null
      }
      label={label}
      format='hh:mm a'
      onChange={onChange}
      slotProps={{
        textField: {
          name,
          variant: 'standard',
          onBlur,
          error: isTouched && !!errorText,
          helperText: isTouched ? errorText : undefined,
          className: cx(classes.inputRoot, className),
          InputLabelProps: { className: classes.label },
          type,
          label,
          required,
          ...textFieldProps,
        },
      }}
      {...otherProps}
    />
  );
});

// Needed because of https://github.com/mui-org/material-ui-pickers/issues/1526
// function jsDateToLocalISO8601DateString(date: Date) {
//   return [
//     String(date.getFullYear()),
//     String(101 + date.getMonth()).substring(1),
//     String(100 + date.getDate()).substring(1),
//   ].join('-');
// }

function dateStringToLocalDate(s: string): Date | null {
  if (!s) return null;
  return parse(s, 'p', new Date());
}
